import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { XMarkIcon, PlayIcon, PauseIcon, ScissorsIcon, ArrowUpTrayIcon, CheckIcon } from '@heroicons/react/24/outline';
import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile } from '@ffmpeg/util';
import { FFmpegCDNLoader } from '../../utils/ffmpegCDNLoader';

interface AudioTrimmerProps {
  /** 原始音频 URL */
  audioUrl: string;
  /** 允许的最小时长（秒），默认 1 */
  minDuration?: number;
  /** 允许的最大时长（秒），默认不限 */
  maxDuration?: number;
  /** 截取完成回调，返回上传后的 URL */
  onConfirm: (url: string) => void;
  /** 取消回调 */
  onCancel: () => void;
}

type Stage = 'idle' | 'loading-ffmpeg' | 'trimming' | 'uploading' | 'done' | 'error';

const fmt = (s: number) => {
  const m = Math.floor(s / 60);
  const sec = (s % 60).toFixed(1).padStart(4, '0');
  return `${m}:${sec}`;
};

export default function AudioTrimmer({
  audioUrl,
  minDuration = 1,
  maxDuration,
  onConfirm,
  onCancel,
}: AudioTrimmerProps) {
  const [duration, setDuration] = useState(0);
  const [startTime, setStartTime] = useState(0);
  const [endTime, setEndTime] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [stage, setStage] = useState<Stage>('idle');
  const [loadProgress, setLoadProgress] = useState(0);
  const [errorMsg, setErrorMsg] = useState('');

  const { t } = useTranslation(['common_ui', 'common']);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const previewAudioRef = useRef<HTMLAudioElement | null>(null);
  void previewAudioRef; // unused, kept for future waveform preview
  const ffmpegRef = useRef<FFmpeg | null>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef<'start' | 'end' | null>(null);
  const animFrameRef = useRef<number>(0);

  // 加载原始音频元数据
  useEffect(() => {
    const audio = new Audio(audioUrl);
    audio.addEventListener('loadedmetadata', () => {
      const d = audio.duration;
      setDuration(d);
      setStartTime(0);
      setEndTime(maxDuration ? Math.min(d, maxDuration) : d);
    });
    audioRef.current = audio;
    return () => { audio.pause(); };
  }, [audioUrl, maxDuration]);

  // 播放预览（在 startTime ~ endTime 范围内）
  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) {
      audio.pause();
      cancelAnimationFrame(animFrameRef.current);
      setPlaying(false);
    } else {
      audio.currentTime = startTime;
      audio.play();
      setPlaying(true);
      const tick = () => {
        if (!audio) return;
        setCurrentTime(audio.currentTime);
        if (audio.currentTime >= endTime) {
          audio.pause();
          setPlaying(false);
          return;
        }
        animFrameRef.current = requestAnimationFrame(tick);
      };
      animFrameRef.current = requestAnimationFrame(tick);
    }
  };

  // 停止播放
  const stopPlay = useCallback(() => {
    audioRef.current?.pause();
    cancelAnimationFrame(animFrameRef.current);
    setPlaying(false);
  }, []);

  // 拖拽手柄逻辑
  const getTimeFromX = (clientX: number): number => {
    const rect = trackRef.current?.getBoundingClientRect();
    if (!rect || duration === 0) return 0;
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    return ratio * duration;
  };

  const onMouseDown = (handle: 'start' | 'end') => (e: React.MouseEvent) => {
    e.preventDefault();
    stopPlay();
    draggingRef.current = handle;
  };

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!draggingRef.current) return;
      const t = getTimeFromX(e.clientX);
      if (draggingRef.current === 'start') {
        const max = maxDuration ? endTime - maxDuration : endTime - minDuration;
        const newStart = Math.max(0, Math.min(t, endTime - minDuration, max < 0 ? Infinity : max));
        setStartTime(newStart);
      } else {
        const min = maxDuration ? startTime + maxDuration : startTime + minDuration;
        const newEnd = Math.min(duration, Math.max(t, startTime + minDuration, min > duration ? -Infinity : min));
        setEndTime(newEnd);
      }
    };
    const onUp = () => { draggingRef.current = null; };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [duration, startTime, endTime, minDuration, maxDuration]);

  // 校验选区时长
  const selectedDuration = endTime - startTime;
  const isValid = selectedDuration >= minDuration && (!maxDuration || selectedDuration <= maxDuration);

  // 执行截取 + 上传
  const handleConfirm = async () => {
    if (!isValid) return;
    setErrorMsg('');

    try {
      // 1. 加载 FFmpeg（如果还没加载）
      if (!ffmpegRef.current) {
        setStage('loading-ffmpeg');
        setLoadProgress(0);
        const loader = new FFmpegCDNLoader((p) => setLoadProgress(p.progress));
        const { ffmpeg } = await loader.loadFFmpeg();
        ffmpegRef.current = ffmpeg;
      }

      const ffmpeg = ffmpegRef.current;

      // 2. 截取音频
      setStage('trimming');
      const audioData = await fetchFile(audioUrl);
      await ffmpeg.writeFile('input.mp3', audioData);
      await ffmpeg.exec([
        '-i', 'input.mp3',
        '-ss', String(startTime.toFixed(3)),
        '-t', String(selectedDuration.toFixed(3)),
        '-c', 'copy',
        'output.mp3'
      ]);
      const outputData = await ffmpeg.readFile('output.mp3') as Uint8Array;
      await ffmpeg.deleteFile('input.mp3');
      await ffmpeg.deleteFile('output.mp3');

      // 3. 上传
      setStage('uploading');
      // 用 slice 复制数据，避免 SharedArrayBuffer 在无 COOP/COEP 环境下报错
      const safeData = outputData.slice(0);
      const blob = new Blob([safeData], { type: 'audio/mpeg' });
      const file = new File([blob], `trimmed_${Date.now()}.mp3`, { type: 'audio/mpeg' });
      const fd = new FormData();
      fd.append('audio', file);
      const res = await fetch('/api/user/upload/audio', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: fd,
      });
      const result = await res.json();
      if (!result.success || !result.data?.url) {
        throw new Error(result.message || t('common_ui:trimmer.uploadFailed'));
      }

      setStage('done');
      onConfirm(result.data.url);
    } catch (err: any) {
      setErrorMsg(err.message || t('common_ui:trimmer.processFailed'));
      setStage('error');
    }
  };

  const startPct = duration > 0 ? (startTime / duration) * 100 : 0;
  const endPct = duration > 0 ? (endTime / duration) * 100 : 100;
  const curPct = duration > 0 ? (currentTime / duration) * 100 : 0;

  const busy = stage === 'loading-ffmpeg' || stage === 'trimming' || stage === 'uploading';

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* 遮罩 */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={!busy ? onCancel : undefined} />

      {/* 面板 */}
      <div className="relative bg-bg-surface border border-border-subtle rounded-xl p-5 space-y-5 w-full max-w-lg shadow-2xl">
        {/* 标题 */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-gray-200 font-medium">
            <ScissorsIcon className="w-4 h-4 text-primary" />
            <span>{t('common_ui:trimmer.title')}</span>
          </div>
          <button type="button" onClick={onCancel} className="text-gray-500 hover:text-gray-300 transition-colors">
            <XMarkIcon className="w-4 h-4" />
          </button>
        </div>

        {/* 时长限制提示 */}
        {(minDuration > 1 || maxDuration) && (
          <p className="text-xs text-gray-500">
            {t('common_ui:trimmer.durationRange', { min: minDuration, max: maxDuration ? t('common_ui:trimmer.durationRangeMax', { max: maxDuration }) : t('common_ui:trimmer.durationRangePlus') })}
          </p>
        )}

        {/* 波形轨道 */}
        <div className="space-y-2">
          <div
            ref={trackRef}
            className="relative h-14 bg-bg-elevated rounded-lg overflow-visible select-none cursor-default"
          >
            {/* 未选中区域遮罩 */}
            <div className="absolute inset-y-0 left-0 bg-black/40 rounded-l-lg" style={{ width: `${startPct}%` }} />
            <div className="absolute inset-y-0 right-0 bg-black/40 rounded-r-lg" style={{ width: `${100 - endPct}%` }} />

            {/* 选中区域高亮 */}
            <div
              className="absolute inset-y-0 bg-primary/20 border-t border-b border-primary/50"
              style={{ left: `${startPct}%`, width: `${endPct - startPct}%` }}
            />

            {/* 播放进度线 */}
            {playing && (
              <div
                className="absolute inset-y-0 w-0.5 bg-white/80 pointer-events-none"
                style={{ left: `${curPct}%` }}
              />
            )}

            {/* 起始手柄 */}
            <div
              className="absolute top-0 bottom-0 w-3 -ml-1.5 flex items-center justify-center cursor-ew-resize z-10 group"
              style={{ left: `${startPct}%` }}
              onMouseDown={onMouseDown('start')}
            >
              <div className="w-1.5 h-8 bg-primary rounded-full group-hover:bg-primary-light transition-colors" />
            </div>

            {/* 结束手柄 */}
            <div
              className="absolute top-0 bottom-0 w-3 -ml-1.5 flex items-center justify-center cursor-ew-resize z-10 group"
              style={{ left: `${endPct}%` }}
              onMouseDown={onMouseDown('end')}
            >
              <div className="w-1.5 h-8 bg-primary rounded-full group-hover:bg-primary-light transition-colors" />
            </div>
          </div>

          {/* 时间标注 */}
          <div className="flex justify-between text-xs text-gray-500 px-0.5">
            <span>0:00.0</span>
            <span>{fmt(duration)}</span>
          </div>
        </div>

        {/* 选区信息 + 播放控制 */}
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={togglePlay}
            disabled={duration === 0 || busy}
            className="flex-shrink-0 w-9 h-9 rounded-full bg-bg-elevated hover:bg-border-default flex items-center justify-center text-gray-200 transition-colors disabled:opacity-40"
          >
            {playing ? <PauseIcon className="w-4 h-4" /> : <PlayIcon className="w-4 h-4" />}
          </button>

          <div className="flex-1 text-sm text-gray-300 space-y-0.5">
            <div className="flex gap-3">
              <span className="text-gray-500">{t('common_ui:trimmer.start')}</span>
              <span className="font-mono">{fmt(startTime)}</span>
              <span className="text-gray-500 ml-2">{t('common_ui:trimmer.end')}</span>
              <span className="font-mono">{fmt(endTime)}</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-gray-500">{t('common_ui:trimmer.duration')}</span>
              <span className={`font-mono text-xs ${isValid ? 'text-green-400' : 'text-red-400'}`}>
                {fmt(selectedDuration)}
              </span>
              {!isValid && (
                <span className="text-xs text-red-400">
                  {t('common_ui:trimmer.durationNeed', { min: minDuration, max: maxDuration ? `~${maxDuration}s` : '+' })}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* 进度状态 */}
        {busy && (
          <div className="space-y-1.5">
            <div className="flex items-center gap-2 text-xs text-gray-400">
              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-primary" />
              <span>
                {stage === 'loading-ffmpeg' && t('common_ui:trimmer.loadingEngine', { progress: loadProgress.toFixed(0) })}
                {stage === 'trimming' && t('common_ui:trimmer.trimming')}
                {stage === 'uploading' && t('common_ui:trimmer.uploading')}
              </span>
            </div>
            {stage === 'loading-ffmpeg' && (
              <div className="h-1 bg-bg-elevated rounded-full overflow-hidden">
                <div className="h-full bg-primary transition-all" style={{ width: `${loadProgress}%` }} />
              </div>
            )}
          </div>
        )}

        {/* 错误提示 */}
        {stage === 'error' && (
          <p className="text-xs text-red-400">{errorMsg}</p>
        )}

        {/* 操作按钮 */}
        <div className="flex gap-3 pt-1">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="flex-1 py-2 text-sm text-gray-400 hover:text-gray-200 bg-bg-elevated hover:bg-border-default rounded-lg transition-colors disabled:opacity-40"
          >
            {t('common:cancel')}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!isValid || busy || duration === 0}
            className="flex-1 py-2 text-sm font-medium text-white bg-primary hover:bg-primary-light rounded-lg transition-colors disabled:opacity-40 flex items-center justify-center gap-1.5"
          >
            {stage === 'done'
              ? <><CheckIcon className="w-4 h-4" />{t('common_ui:trimmer.done')}</>
              : <><ArrowUpTrayIcon className="w-4 h-4" />{t('common_ui:trimmer.confirm')}</>
            }
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
